import React from "react";
import Select, { components } from "react-select";
import { constantStyleForSelect } from "../../../components/CustomSelect";

const SELECT_ALL_VALUE = "__all__";

const getOptionsWithSelectAll = (options) => [
  { label: "Select All", value: SELECT_ALL_VALUE },
  ...options,
];

const CheckboxOption = (props) => {
  const isDisabled = props.selectProps.isOptionDisabled && props.selectProps.isOptionDisabled(props.data);
  const isSelectAll = props.data.value === SELECT_ALL_VALUE;

  const allOptions = props.selectProps.options.filter(
    (opt) =>
      opt.value !== SELECT_ALL_VALUE &&
      (!props.selectProps.isOptionDisabled || !props.selectProps.isOptionDisabled(opt))
  );

  const selectedValues = props.selectProps.value || [];

  const allSelected =
    allOptions.length > 0 &&
    allOptions.every((opt) =>
      selectedValues.find((sel) => sel.value === opt.value)
    );

  const isChecked = isSelectAll ? allSelected : props.isSelected;

  const handleOptionClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isDisabled) return;

    if (isSelectAll) {
      props.selectProps.onChange(allSelected ? [] : allOptions, {
        action: "select-option",
        option: props.data,
      });
    } else {
      props.selectOption(props.data);
    }
  };

  return (
    <components.Option
      {...props}
      innerRef={props.innerRef}
      innerProps={{
        ...props.innerProps,
        onClick: handleOptionClick,
        style: {
          cursor: isDisabled ? "not-allowed" : "pointer",
          opacity: isDisabled ? 0.5 : 1,
          display: "flex",
          alignItems: "center",
        },
      }}
    >
      <input
        type="checkbox"
        checked={isChecked}
        disabled={isDisabled}
        onChange={() => {}}
        style={{
          marginRight: 8,
          cursor: isDisabled ? "not-allowed" : "pointer",
        }}
      />
      {props.label}
    </components.Option>
  );
};

const ValueContainer = ({ children, ...props }) => {
  const selectedValues = props.selectProps.value || [];
  const hasValue = selectedValues.length > 0;

  let summaryText = "";
  if (hasValue) {
    if (selectedValues.length <= 2) {
      summaryText = selectedValues.map((v) => v.label).join(", ");
    } else {
      summaryText = `${selectedValues
        .slice(0, 2)
        .map((v) => v.label)
        .join(", ")} +${selectedValues.length - 2} more`;
    }
  }

  return (
    <components.ValueContainer {...props}>
      <div style={{ display: "flex", alignItems: "center", width: "100%", height: "30px", overflow: "hidden" }}>
        {hasValue && (
          <span
            style={{
              color: "#999",
              marginRight: "8px",
              fontSize: "14px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              minWidth: 0,
              flexShrink: 1,
            }}
          >
            {summaryText}
          </span>
        )}
        <div style={{ flex: "0 0 auto", height: "30px", overflow: "hidden" }}>{children}</div>
      </div>
    </components.ValueContainer>
  );
}; 

const MultiSelectClientDropdown = ({
  options = [],
  value = [],
  onChange,
  placeholder = "Select options...",
  getThemeColor,
  isDisabled = false, 
  disabledOptions = [],
  addSelectAll,
  useMenuPortal = false,
  noOptionsMessage = "No options available",
}) => {
  const allOptionsWithSelectAll =
    addSelectAll && options.length > 0
      ? getOptionsWithSelectAll(options)
      : options;
  
  const filteredValue = value.filter(item => item.value !== SELECT_ALL_VALUE);

  return (
    <Select
      classNamePrefix="react-select"
      isMulti
      isSearchable
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      isDisabled={isDisabled}
      options={allOptionsWithSelectAll}
      noOptionsMessage={() => noOptionsMessage}
      menuPortalTarget={useMenuPortal ? document.body : undefined}
      menuPosition={useMenuPortal ? "fixed" : "absolute"}
      components={{
        Option: CheckboxOption,
        ValueContainer,
        MultiValue: () => null,
      }}
      value={filteredValue}
      onChange={onChange}
      isOptionDisabled={(option) =>
        disabledOptions.includes(option.value)
      }
      styles={{
        ...constantStyleForSelect(getThemeColor),
        ...(useMenuPortal
          ? {
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }
          : {}),
        control: (base) => ({
          ...base,
          minHeight: "38px",
          maxHeight: "38px",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          display: "flex",
          alignItems: "center",
        }),
        valueContainer: (base) => ({
          ...base,
          maxHeight: "38px",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          padding: "0 8px",
        }),
        placeholder: (base, state) => ({
          ...base,
          margin: 0,
          position: "absolute",
          top: "50%",
          transform: "translateY(-50%)",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          opacity: state.isFocused || state.selectProps.menuIsOpen ? 0 : 1,
        }),
        input: (base) => ({
          ...base,
          margin: 0,
          padding: 0,
          top: "50%",
          transform: "translateY(-50%)",
          position: "relative",
        }),
      }}
      placeholder={placeholder}
    />
  );
};

export default MultiSelectClientDropdown;
